import type { SourceFolder, SourceKind } from "@trip/contracts/bridge";
import {
  Camera,
  Download,
  Folder,
  FolderPlus,
  Image,
  MessageCircle,
  ScreenShare,
  ShieldCheck,
  Smartphone,
} from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import type { Trip } from "../lib/api.ts";
import { nativeBridge, type Bridge } from "../lib/bridge.ts";
import { formatBytes } from "../lib/transfer.ts";
import { Banner, Card, EmptyState, ICON, Pill } from "../ui/index.ts";

/** The icon for a source: what it is, never its path. */
export function SourceIcon({ kind, name }: { kind: SourceKind; name: string }) {
  if (kind === "CAMERA") return <Smartphone {...ICON} />;
  if (kind === "SCREEN") return <ScreenShare {...ICON} />;
  if (kind === "CARD") return <Camera {...ICON} />;
  if (kind === "SCREENSHOTS") return <Image {...ICON} />;
  if (/whatsapp|telegram|signal|messages/i.test(name)) return <MessageCircle {...ICON} />;
  if (/download/i.test(name)) return <Download {...ICON} />;
  return <Folder {...ICON} />;
}

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/**
 * Sync sources: which folders on this phone the trip may receive media from. Only switched-on
 * folders reach the Sync grid; the rest of the camera roll stays on the phone. The choice is kept
 * natively, per trip; folder paths never reach the page.
 */
export function SourcesPage({
  trip,
  bridge = nativeBridge(),
}: {
  trip: Trip;
  bridge?: Bridge | null;
}) {
  const [folders, setFolders] = useState<SourceFolder[] | null>(null);
  const [blocked, setBlocked] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!bridge) return;
    try {
      const r = await bridge.call("sources.folders", { tripId: trip.id });
      if ("blocked" in r) setBlocked(true);
      else {
        setBlocked(false);
        setFolders(r.folders);
      }
    } catch (e) {
      setError((e as Error).message);
    }
  }, [bridge, trip.id]);

  useEffect(() => void load(), [load]);

  if (!bridge) {
    return (
      <EmptyState
        icon={<Smartphone size={24} strokeWidth={1.5} />}
        title="Sources are chosen in the Android app"
        body="Open Trip Media on your phone to choose which folders this trip may receive media from."
      />
    );
  }
  if (blocked) {
    return (
      <EmptyState
        title="Allow access to photos and videos"
        body="The app needs to read your photos and videos to list the folders you can share. It never changes or deletes them."
        action={{ label: "Allow access", onClick: () => void load() }}
      />
    );
  }

  async function setOn(f: SourceFolder, on: boolean) {
    setError(null);
    const flip = (value: boolean) =>
      setFolders((all) => all && all.map((x) => (x.id === f.id ? { ...x, on: value } : x)));
    flip(on);
    try {
      await bridge!.call("sources.setFolder", { tripId: trip.id, sourceId: f.id, on });
    } catch (e) {
      flip(!on);
      setError((e as Error).message);
    }
  }

  async function addFolder() {
    setError(null);
    try {
      const r = await bridge!.call("sources.addFolder", { tripId: trip.id });
      if (r.added) await load();
    } catch (e) {
      setError((e as Error).message);
    }
  }

  const shared = (folders ?? []).filter((f) => f.on);
  const notShared = (folders ?? []).filter((f) => !f.on);
  const row = (f: SourceFolder) => (
    <div className="row-item" key={f.id}>
      <span className="row-icon" aria-hidden="true">
        <SourceIcon kind={f.kind} name={f.name} />
      </span>
      <span className="row-main">
        <span className="row-title">{f.name}</span>
        <span className="row-detail">
          {f.on
            ? `${plural(f.newFiles, "new file")} · ${formatBytes(f.newBytes)}`
            : f.files > 0
              ? `${plural(f.files, "file")} from the trip · ${formatBytes(f.bytes)}`
              : "No media from the trip"}
          {f.removable && !f.connected ? " · Not connected" : ""}
        </span>
      </span>
      <input
        type="checkbox"
        role="switch"
        className="switch"
        checked={f.on}
        aria-label={`Share ${f.name}`}
        onChange={(e) => void setOn(f, e.target.checked)}
      />
    </div>
  );

  return (
    <div className="stack sources-page">
      {error && <Banner tone="bad" message={error} />}
      <div className="shield-note">
        <p className="shield-title">
          <ShieldCheck {...ICON} aria-hidden="true" />
          Only your authorized trip media
        </p>
        <p className="muted small">
          {trip.name} only ever receives media from the folders you switch on here. The rest of your
          camera roll stays private.
        </p>
      </div>
      {folders && (
        <>
          <Card
            title={`Shared with ${trip.name}`}
            label={`Shared with ${trip.name}`}
            actions={<Pill tone="good">{plural(shared.length, "folder")} on</Pill>}
          >
            {shared.length === 0 ? (
              <p className="muted small" style={{ margin: 0 }}>
                No folder is shared yet; nothing will be offered for this trip.
              </p>
            ) : (
              <div className="rows">{shared.map(row)}</div>
            )}
          </Card>
          <Card title="Not shared" label="Not shared" actions={<Pill>Stays on phone</Pill>}>
            {notShared.length === 0 ? (
              <p className="muted small" style={{ margin: 0 }}>
                Every folder with photos or videos is shared.
              </p>
            ) : (
              <div className="rows">{notShared.map(row)}</div>
            )}
          </Card>
        </>
      )}
      <button className="button add-folder" type="button" onClick={() => void addFolder()}>
        <FolderPlus {...ICON} aria-hidden="true" />
        Add a folder
      </button>
      <p className="muted small">
        Turning a folder on only offers media captured during the trip dates — nothing older,
        nothing newer. Turning it off stops future syncs; files already uploaded stay with the trip.
      </p>
    </div>
  );
}
